define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../System/Vec3Obj',
    '../System/ColorObj',
    '../Enum/EnumPathPlayMode',
    '../Enum/EnumPathPlayStatus',
    '../Enum/EnumRenderMode',
    '../AI/Path'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    Vec3Obj,
    ColorObj,
    EnumPathPlayMode,
    EnumPathPlayStatus,
    EnumRenderMode,
    Path
) {
    'use strict'

    /**
     * 长方体。
     * <br /><br />
     * <div align='center'>
     * <img src='images/GeoCuboid.png' width='900' height='650' /><br />
     * 长方体效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/GeoCuboid.html|CooWasm GeoCuboid demo}
     * @alias GeoCuboid
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {Vec3Obj} [props.translation=new Vec3Obj(0.0, 0.0, 0.0)] x、y、z轴偏移值。
     * @param {Vec3Obj} [props.scale=new Vec3Obj(1.0, 1.0, 1.0)] x、y、z轴比例值。
     * @param {Vec3Obj} [props.rotation=new Vec3Obj(0.0, 0.0, 0.0)] x、y、z轴旋转值。
     * @param {Vec3Obj} [props.location=null] 地理位置坐标值，默认为当前视点目标点位置。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {Boolean} [props.bCast=false] 是否参与阴影投射。
     * @param {Boolean} [props.bReceive=false] 是否接受阴影附着效果。
     * @param {Boolean} [props.renderMode=EnumRenderMode.NORMAL] 渲染模式。
     * @param {ColorObj} [props.pickedColor=new ColorObj(1.0,0.0,0.0,0.6)] 被拾取颜色。
     * @param {EnumPathPlayStatus} [props.playStatus=EnumPathPlayStatus.STOP] 运动状态。
     * @param {EnumPathPlayMode} [props.playMode=EnumPathPlayMode.ONCE] 运动模式。
     * @param {Path} [props.path=null] 绑定的路径对象。
     * @param {Number} [props.moveSpeed=5.0] 移动速度，单位：m/s。
     * @param {Number} [props.rotateSpeed=40.0] 转弯速度，单位：角度/s。
     * @param {Number} [props.interval=10] 间隔帧数，用于控制positionCallback触发间隔。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @param {CallbackString} [wayPointCallback=null] 沿着路径运动时，经过关键路径点时，返回索引。
     * @param {CallbackString} [positionCallback=null] 沿着路径运动时，按照一定时间间隔返回经纬度地理坐标。
     * @example
     * var geoCuboid = new CooWasm.GeoCuboid({},(state)=>{console.log("load status:" + state)});
     */
    class GeoCuboid {
        /**
         * @description 长方体x、y、z轴偏移值。
         * @type {Vec3Obj}
         */
        translation
        /**
         * @description 长方体x、y、z轴比例值。
         * @type {Vec3Obj}
         */
        scale
        /**
         * @description 长方体x、y、z轴旋转值。
         * @type {Vec3Obj}
         */
        rotation
        /**
         * @description 长方体地理位置坐标值，默认为当前视点目标点位置。
         * @type {Vec3Obj}
         */
        location
        /**
         * @description 长方体地理位置坐标参考系。
         * @type {String}
         * @readonly
         */
        spatialReference = "EPSG:4326"
        /**
         * @description 长方体显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 是否参与阴影投射。
         * @type {Boolean}
         * @default false
         */
        bCast = false
        /**
         * @description 是否接受阴影附着效果。
         * @type {Boolean}
         * @default false
         */
        bReceive = false
        /**
         * @description 渲染模式。
         * @type {EnumRenderMode}
         * @default EnumRenderMode.NORMAL
         */
        renderMode
        /**
         * @description 被拾取颜色。
         * @type {ColorObj}
         * @default new ColorObj(1.0,0.0,0.0,0.6)
         */
        pickedColor
        /**
         * @description 物体运动状态。
         * @type {EnumPlayStatus}
         */
        playStatus
        /**
         * @description 物体运动模式。
         * @type {EnumPathPlayMode}
         */
        playMode
        /**
         * @description 移动速度，单位：m/s。
         * @type {Number}
         */
        moveSpeed
        /**
         * @description 转弯速度，单位：角度/s。
         * @type {Number}
         */
        rotateSpeed
        /**
         * @description 间隔帧数，用于控制positionCallback触发间隔。
         * @type {Number}
         */
        interval
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 索引回调。
         * @type {CallbackString}
         */
        bIndexCallback
        /**
         * @description 位置回调。
         * @type {CallbackString}
         */
        bPositionCallback
        /**
         * @description 常亮状态。
         * @type {Boolean}
         * @default false
         */
        bHighlight = false
        /**
         * @description 长方体对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 长方体AI组件uuid。
         * @type {String}
         * @readonly
         */
        componentAIUuid = null
        /**
         * @description 长方体拾取组件uuid。
         * @type {String}
         * @readonly
         */
        componentPickRenderUuid = null
        /**
         * @description 长方体高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description 长方体渲染组件uuid。
         * @type {String}
         * @readonly
         */
        componentRenderUuid = null
        /**
         * @description path对象。
         * @type {Path}
         */
        path = null
        /**
         * @description path对象的avatar uuid。
         * @type {String}
         * @readonly
         */
        pathAvatarUuid = null
        /**
         * @description path对象的component uuid。
         * @type {String}
         * @readonly
         */
        pathComponentUuid = null
        /**
         * @description GeoCuboid在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "GeoCuboid"
        static avatarType = "SpaceAvatar.Geometry.GeoCuboidAvatar"
        static componentAIType = "deSMP.AvatarComponent.AIComponentEx"
        static componentPickRenderType = "deSMP.AvatarComponent.PickRenderComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
        static componentRenderType = "AvatarComponent.RenderComponent.RenderComponent"
        constructor(props, callback, wayPointCallback, positionCallback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            let bSync = true
            this.translation = defaultValue(props.translation, new Vec3Obj(0.0, 0.0, 0.0))
            this.scale = defaultValue(props.scale, new Vec3Obj(1.0, 1.0, 1.0))
            this.rotation = defaultValue(props.rotation, new Vec3Obj(0.0, 0.0, 0.0))
            this.location = defaultValue(props.location, null)
            this.bVisible = defaultValue(props.bVisible, true)
            this.bCast = defaultValue(props.bCast, false)
            this.bReceive = defaultValue(props.bReceive, false)
            this.renderMode = defaultValue(props.renderMode, EnumRenderMode.NORMAL)
            this.pickedColor = defaultValue(props.pickedColor, new ColorObj(1.0, 0.0, 0.0, 0.6))
            this.playStatus = defaultValue(props.playStatus, EnumPathPlayStatus.STOP)
            this.playMode = defaultValue(props.playMode, EnumPathPlayMode.ONCE)
            this.path = defaultValue(props.path, null)
            this.moveSpeed = defaultValue(props.moveSpeed, 5.0)
            this.rotateSpeed = defaultValue(props.rotateSpeed, 40.0)
            this.interval = defaultValue(props.interval, 10)
            // this.minAltitude = defaultValue(props.minAltitude, 0.0);
            // this.maxAltitude = defaultValue(props.maxAltitude, 1000000.0);
            //create avatars
            let avatarCategoryName = "SpaceAvatar.Geometry.GeoCuboidAvatar"
            let aiType = "deSMP.AvatarComponent.AIComponentEx"
            let prType = "deSMP.AvatarComponent.PickRenderComponent"
            let avcType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
            let renderType = "AvatarComponent.RenderComponent.RenderComponent"
            let categoryNames = aiType + "#$%" + prType + "#$%" + avcType + "#$%" + renderType + "#$%"
            let avatarProps = ""
            let componetProps = ""
            //avatar props
            avatarProps += this.toStr("bVisible", this.bVisible, "Visible")
            avatarProps += this.toStr("renderMode", this.renderMode, "RenderMode")
            avatarProps += this.toStr("translation", this.translation, "Translation")
            avatarProps += this.toStr("scale", this.scale, "Scale")
            avatarProps += this.toStr("rotation", this.rotation, "Rotation")
            if (defined(props.location)) {
                bSync = false
                this.location = props.location
                avatarProps += "Location:" + this.spatialReference + "/" + this.location.toStr() + "|"
            }
            //componet props
            let aiComponentProps = ""
            //如有路径相应信息，则创建action tree
            if (defined(this.path)) {
                this.bIndexCallback = wayPointCallback
                this.bPositionCallback = positionCallback
                let treeInfo = this.toXml()
                console.log(treeInfo)
                aiComponentProps += "BehaviorTreeInfo" + ":" + treeInfo + "|"
                aiComponentProps += this.toStr("playStatus", this.playStatus, "BehaviorTreePlayState")
            }
            componetProps += aiComponentProps + "#$%"

            let prComponentProps = ""
            prComponentProps += this.toStr("bHighlight", this.bHighlight, "Mode")
            prComponentProps += this.toStr("pickedColor", this.pickedColor, "Color")
            componetProps += prComponentProps + "#$%"

            let avcComponentProps = ""
            avcComponentProps += this.toStr("minAltitude", this.minAltitude, "MinVisibleAltitude")
            avcComponentProps += this.toStr("maxAltitude", this.maxAltitude, "MaxVisibleAltitude")
            componetProps += avcComponentProps + "#$%"

            let renderComponentProps = ""
            renderComponentProps += this.toStr("bCast", this.bCast, "CastShadow")
            renderComponentProps += this.toStr("bReceive", this.bReceive, "ReceiveShadow")
            componetProps += renderComponentProps + "#$%"

            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(categoryNames), encode(componetProps), bSync, this.bVisible, defined(callback))
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentAIUuid = idArray[1]
                this.componentPickRenderUuid = idArray[2]
                this.componentAltitudeVisCtrlUuid = idArray[3]
                this.componentRenderUuid = idArray[4]
                callbackSystem.register("WayPoint", this.avatarUuid, (index) => {
                    if (defined(wayPointCallback)) {
                        wayPointCallback(index)
                    }
                })
                callbackSystem.register("CurrentPosition", this.avatarUuid, (position) => {
                    if (defined(wayPointCallback)) {
                        positionCallback(position)
                    }
                })
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                    Element.prototype.Map.geoCuboids.push(this)
                })
                //console.log("geo avatarUuid:" + this.avatarUuid + " componentAIUuid:" + this.componentAIUuid + " componentPickRenderUuid:" + this.componentPickRenderUuid);
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {Vec3Obj} [props.translation] x、y、z轴偏移值。
         * @param {Vec3Obj} [props.scale] x、y、z轴比例值。
         * @param {Vec3Obj} [props.rotation] x、y、z轴旋转值。
         * @param {Vec3Obj} [props.location] 地理位置坐标值，默认为当前视点目标点位置。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {Boolean} [props.bCast] 是否参与阴影投射。
         * @param {Boolean} [props.bReceive] 是否接受阴影附着效果。
         * @param {Boolean} [props.renderMode] 渲染模式。
         * @param {ColorObj} [props.pickedColor] 被拾取颜色。
         * @param {EnumPathPlayStatus} [props.playStatus] 运动状态。
         * @param {EnumPathPlayMode} [props.playMode] 运动模式。
         * @param {Path} [props.path] 绑定的路径对象。
         * @param {Number} [props.moveSpeed] 移动速度，单位：m/s。
         * @param {Number} [props.rotateSpeed] 转弯速度，单位：角度/s。
         * @param {Number} [props.interval] 间隔帧数，用于控制positionCallback触发间隔。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {CallbackBoolean} [callback=null] 更新成功后触发的函数回调。
         * @param {CallbackString} [wayPointCallback=null] 沿着路径运动时，经过关键路径点时，返回索引。
         * @param {CallbackString} [positionCallback=null] 沿着路径运动时，按照一定时间间隔返回经纬度地理坐标。
         * @example
         * geoCuboid.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback, wayPointCallback, positionCallback) {
            let avatarProps = ""
            let componentProps = ""
            let aiComponentProps = ""
            let prComponentProps = ""
            let avcComponentProps = ""
            let renderComponentProps = ""
            let uuids = ""
            if (defined(wayPointCallback) && wayPointCallback !== this.bIndexCallback) {
                this.bIndexCallback = wayPointCallback
            }
            if (defined(positionCallback) && positionCallback !== this.bPositionCallback) {
                this.bPositionCallback = positionCallback
            }
            if (defined(props)) {
                avatarProps += this.toStr("translation", props.translation, "Translation")
                avatarProps += this.toStr("scale", props.scale, "Scale")
                avatarProps += this.toStr("rotation", props.rotation, "Rotation")
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
                avatarProps += this.toStr("renderMode", props.renderMode, "RenderMode")
                if (!props.bHighlight)//常亮状态下不记录模式变化
                    this.markRenderMode = this.renderMode
                if (defined(props.location)) {
                    this.location = props.location
                    avatarProps += "Location:" + this.spatialReference + "/" + this.location.toStr() + "|"
                }
                //ai
                if (!defined(this.path) && defined(props.path)) {
                    this.path = props.path
                    let needToUpdateTree = true
                    if (defined(props.moveSpeed) && props.moveSpeed !== this.moveSpeed) {
                        this.moveSpeed = props.moveSpeed
                    }
                    if (defined(props.rotateSpeed) && props.rotateSpeed !== this.rotateSpeed) {
                        this.rotateSpeed = props.rotateSpeed
                    }
                    if (defined(props.interval) && props.interval !== this.interval) {
                        this.interval = props.interval
                    }
                    if (defined(props.playMode) && props.playMode !== this.playMode) {
                        this.playMode = props.playMode
                    }
                    aiComponentProps += "BehaviorTreeInfo" + ":" + this.toXml() + "|"
                } else if (defined(props.path)) {
                    let needToUpdateTree = true
                    if (defined(props.moveSpeed) && props.moveSpeed !== this.moveSpeed) {
                        this.moveSpeed = props.moveSpeed
                        needToUpdateTree = true
                    }
                    if (defined(props.rotateSpeed) && props.rotateSpeed !== this.rotateSpeed) {
                        this.rotateSpeed = props.rotateSpeed
                        needToUpdateTree = true
                    }
                    if (defined(props.interval) && props.interval !== this.interval) {
                        this.interval = props.interval
                        needToUpdateTree = true
                    }
                    if (defined(props.playMode) && props.playMode !== this.playMode) {
                        this.playMode = props.playMode
                        needToUpdateTree = true
                    }
                    let treeInfo = needToUpdateTree ? this.toXml() : this.treeInfo
                    if (needToUpdateTree) {
                        aiComponentProps += "BehaviorTreeInfo" + ":" + treeInfo + "|"
                    }
                }

                aiComponentProps += this.toStr("playStatus", props.playStatus, "BehaviorTreePlayState")
                
                //pickrender
                prComponentProps += this.toStr("bHighlight", props.bHighlight, "Mode")
                prComponentProps += this.toStr("pickedColor", props.pickedColor, "Color")

                //altitudevisctrl
                avcComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                avcComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                //render
                renderComponentProps += this.toStr("bCast", props.bCast, "CastShadow")
                renderComponentProps += this.toStr("bReceive", props.bReceive, "ReceiveShadow")

                uuids += this.componentAIUuid + "#$%"
                uuids += this.componentPickRenderUuid + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"
                uuids += this.componentRenderUuid + "#$%"
                componentProps += aiComponentProps + "#$%"
                componentProps += prComponentProps + "#$%"
                componentProps += avcComponentProps + "#$%"
                componentProps += renderComponentProps + "#$%"

                callbackSystem.register("WayPoint", this.avatarUuid, (index) => {
                    if (defined(this.bIndexCallback)) {
                        this.bIndexCallback(index)
                    }
                })
                callbackSystem.register("CurrentPosition", this.avatarUuid, (position) => {
                    if (defined(this.bPositionCallback)) {
                        this.bPositionCallback(position)
                    }
                })
                callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        /**
         * 生成符合RFC4122标准的UUID (版本4)
         * @returns {String} 返回格式为'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'的UUID
         */
        generateUuid() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0
                const v = c === 'x' ? r : (r & 0x3 | 0x8)
                return v.toString(16)
            })
        }

        toXml() {
            if (!defined(this.path)) {
                console.log("need Path, action tree create failed!")
                return ""
            }
            let behaviorTreeId = this.generateUuid()
            let sequenceId = this.generateUuid()
            let wayPointActionId = this.generateUuid()
            let moveActionId = this.generateUuid()
            let treeInfo =
                `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
                <behaviorTrees>
                    <behaviorTree id="${behaviorTreeId}" name="behavior" root="true">
                        <sequence id="${sequenceId}">
                        <wayPointAction id="${wayPointActionId}" pathComID="${this.path.avatarUuid}/${this.path.componentUuid}" repeatType="${this.playMode}" arrivalCallback="${defined(this.bIndexCallback)}" />
                        <bezierMoveAction id="${moveActionId}" moveTargetType="2" moveSpeed="${this.moveSpeed}" faceTargetType="2" customPitch="0.0" customAzimuth="180.0" turnSpeed="${this.rotateSpeed}" callbackInterval="${this.interval}" />
                        </sequence>
                    </behaviorTree>
                </behaviorTrees>`
            return treeInfo
        }
        /**
         * 从XML中提取CDATA内容
         * @param {String} xmlString 包含CDATA部分的XML字符串
         * @returns {String} CDATA内容，如果没有找到则返回空字符串
         */
        extractCDATAContent(xmlString) {
            // 匹配CDATA部分的正则表达式
            const cdataRegex = /<!\[CDATA\[([\s\S]*?)\]\]>/g

            const matches = cdataRegex.exec(xmlString)
            if (matches && matches.length > 1) {
                return matches[1]
            }

            return ''
        }
        fromXml(xml) {
            if (!defined(xml) || xml.trim() === "") {
                console.warn("Invalid XML provided to fromXml")
                return
            }
            xml = this.extractCDATAContent(xml)
            try {
                // 创建XML文档对象
                let xmlDoc = null
                if (window.DOMParser) {
                    const parser = new DOMParser()
                    xmlDoc = parser.parseFromString(xml, "text/xml")
                    // 检查解析错误
                    const parseError = xmlDoc.getElementsByTagName("parsererror")
                    if (parseError.length > 0) {
                        console.log("XML parsing error: " + parseError[0].textContent)
                    }
                } else {
                    xmlDoc = new ActiveXObject("Microsoft.XMLDOM")
                    xmlDoc.async = false
                    xmlDoc.loadXML(xml)

                    if (xmlDoc.parseError.errorCode !== 0) {
                        console.log("XML parsing error: " + xmlDoc.parseError.reason)
                    }
                }
                // 提取所需元素
                const behaviorTrees = xmlDoc.getElementsByTagName("behaviorTrees")[0]
                if (!behaviorTrees) console.log("Missing behaviorTrees element")
                const behaviorTree = behaviorTrees.getElementsByTagName("behaviorTree")[0]
                if (!behaviorTree) console.log("Missing behaviorTree element")
                const sequence = behaviorTree.getElementsByTagName("sequence")[0]
                if (!sequence) console.log("Missing sequence element")
                const wayPointAction = sequence.getElementsByTagName("wayPointAction")[0]
                const moveAction = sequence.getElementsByTagName("bezierMoveAction")[0]
                if (!wayPointAction || !moveAction) {
                    console.log("Missing required action elements")
                }
                // 获取playMode
                const repeatType = wayPointAction.getAttribute("repeatType")
                this.playMode = defined(repeatType) ? Number(repeatType) : this.playMode
                // 获取moveSpeed并确保是数值类型
                const moveSpeed = moveAction.getAttribute("moveSpeed")
                this.moveSpeed = defined(moveSpeed) ? parseFloat(moveSpeed) : this.moveSpeed
                // 获取rotateSpeed并确保是数值类型
                const turnSpeed = moveAction.getAttribute("turnSpeed")
                this.rotateSpeed = defined(turnSpeed) ? parseFloat(turnSpeed) : this.rotateSpeed
                // 获取interval并确保是数值类型
                const callbackInterval = moveAction.getAttribute("callbackInterval")
                this.interval = defined(callbackInterval) ? parseInt(callbackInterval, 10) : this.interval
                // 返回解析后的属性对象，方便调用者使用
                return {
                    playMode: this.playMode,
                    moveSpeed: this.moveSpeed,
                    rotateSpeed: this.rotateSpeed,
                    interval: this.interval
                }
            } catch (error) {
                console.error("Error parsing XML:", error)
                // 发生错误时返回undefined而不是部分解析的结果
                return undefined
            }
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * geoCuboid.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * geoCuboid.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                } if (name == "RenderMode") {
                    this.renderMode = Number(value)
                } else if (name == "Scale") {
                    this.scale = Vec3Obj.fromStr(value)
                } else if (name == "Rotation") {
                    this.rotation = Vec3Obj.fromStr(value)
                } else if (name == "Translation") {
                    this.translation = Vec3Obj.fromStr(value)
                } else if (name == "Location") {
                    let arr = value.split('/')
                    this.spatialReference = "EPSG:4326"//arr[0];
                    this.location = Vec3Obj.fromStr(arr[1])
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "AIComponentEx") {
                    this.componentAIUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "BehaviorTreeInfo") {
                            let treeInfo = this.fromXml(value)
                        } else if (name == "BehaviorTreePlayState") {
                            this.playStatus = Number(value)
                        }
                    }
                } else if (pName == "PickRenderComponent") {
                    this.componentPickRenderUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "Mode") {
                            if (value == "1") {
                                this.bHighlight = true
                            } else {
                                this.bHighlight = false
                            }
                        } else if (name == "Color") {
                            this.pickedColor = ColorObj.fromStr(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                } else if (pName == "RenderComponent") {
                    this.componentRenderUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "CastShadow") {
                            if (value == "true" || value == "1") {
                                this.bCast = true
                            } else {
                                this.bCast = false
                            }
                        } else if (name == "ReceiveShadow") {
                            if (value == "true" || value == "1") {
                                this.bReceive = true
                            } else {
                                this.bReceive = false
                            }
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 与路径绑定，将按照路径线运动。
         * @param {Path} path Path对象。
         * @param {Boolean} bPlay 是否播放。
         * @param {CallbackBoolean} [callback=null] 更新成功后触发的函数回调。
         * @param {CallbackString} [wayPointCallback=null] 沿着路径运动时，经过关键路径点时，返回索引。
         * @param {CallbackString} [positionCallback=null] 沿着路径运动时，按照一定时间间隔返回经纬度地理坐标。
         * @example
         * var path = new CooWasm.Path({
         *      points:[
         *          new CooWasm.Vec3Obj(120.14809914543076, 30.162721658047321, 12.651933761499823),
         *          new CooWasm.Vec3Obj(120.15027113314304, 30.163552581628441, 32.567664995975790),
         *          new CooWasm.Vec3Obj(120.15215840759309, 30.159521920633519, 10.357548876851798),
         *          new CooWasm.Vec3Obj(120.15043531969498, 30.158788468192203, 9.7634695854038007)
         *      ]
         * })
         * geoCuboid.bindPath(path, true);
         */
        bindPath(path, bPlay, callback, wayPointCallback, positionCallback) {
            if (!(path instanceof Path)) {
                throw new Error('param must be an instance of Path')
            }
            let status = 0
            if (bPlay) {
                status = EnumPathPlayStatus.PLAY
                //Element.prototype.NativeAPI.SetAvatarOrientation(encode(this.avatarUuid), path.points[0].x, path.points[0].y, path.points[0].z, path.points[1].x, path.points[1].y, path.points[1].z)
            } else {
                status = EnumPathPlayStatus.STOP
            }
            this.update({
                path: path,
                playStatus: status
            }, state => {
                if (defined(callback)) {
                    callback(state)
                }
            }, index => {
                if (defined(wayPointCallback)) {
                    wayPointCallback(index)
                }
            }, position => {
                if (defined(positionCallback)) {
                    positionCallback(position)
                }
            })
        }
        /**
         * 将对象设置为高亮状态，此时不可拾取。
         * @param {Boolean} state 是否高亮。
         * @param {CallbackBoolean} [callback=null] 设置成功后触发的函数回调。
         */
        setHighlight(state, callback) {
            if (state) {
                this.markRenderMode = this.renderMode
                this.update({
                    renderMode: EnumRenderMode.NULL,
                    bHighlight: 1
                }, callback)
            } else {
                this.update({
                    renderMode: this.markRenderMode,
                    bHighlight: 0
                }, callback)
            }
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * geoCuboid = geoCuboid.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.geoCuboids = Element.prototype.Map.geoCuboids.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("WayPoint", this.avatarUuid)
                callbackSystem.remove("CurrentPosition", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentAIUuid = null
                this.componentPickRenderUuid = null
                this.componentAltitudeVisCtrlUuid = null
                this.componentRenderUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return GeoCuboid
})